'use client'

import { Fragment, useState } from 'react'
import type { ComponentType } from 'react'
import { AlertCircle, Check, ChevronRight, Loader2, Target as TargetIcon, X } from 'lucide-react'
import type { ProcessedTheme } from './ProcessedEntry/DomainEntry/processedTypes'
import { watchClasses } from './ProcessedEntry/DomainEntry/processedTypes'

// message: a sentence for a person ("Promoted — entry … created"), shown in
// the run history in place of the raw data.
export type InvestigationActionResult = { success: boolean; data?: unknown; error?: string; message?: string }

export type InvestigationTone = 'emerald' | 'red' | 'amber' | 'sky'

export type InvestigationAction = {
  id: string
  label: string
  description: string
  icon: ComponentType<{ className?: string }>
  // Omit to render a visible-but-disabled "Soon" row — every dashboard gets
  // the same action list even before every action has a real backend behind
  // it, rather than each caller hand-picking which subset to show.
  run?: (target: string, input?: string, choice?: string) => Promise<InvestigationActionResult>
  // Most actions here are OSINT-style lookups against the shared Target
  // field (domain/sender/phone) and stay gated on it being non-empty by
  // default. An entry-level action that doesn't consume `target` at all
  // (e.g. Reclassify, which acts on whichever entry the sidebar is already
  // open against) should set this false so it isn't blocked by an unrelated
  // empty field — run() still receives whatever's in Target, it just isn't
  // required to be non-empty first.
  requiresTarget?: boolean
  // For an action whose run() only starts background work (e.g. Browser
  // Load: python answers 202 "Analysis started" and does the work in a
  // thread). Called with run()'s accepted result; the row keeps spinning
  // until it resolves with the real outcome. Without it, "accepted" was
  // shown as a green success and a run that died later looked finished.
  awaitOutcome?: (target: string, accepted: InvestigationActionResult) => Promise<InvestigationActionResult>
  // A decision on the open record (promote, delete...) asks before it runs:
  // the first click turns the row into this confirm button + Cancel.
  confirm?: string
  // The action needs a few words from the person (a discard reason): the
  // row opens a text field; run() receives it as `input`.
  input?: { placeholder: string; minLength?: number }
  // The action needs a pick (the bin a hand-set verdict goes in): the row
  // opens a select above the text field; run() receives it as `choice`.
  choice?: { label: string; options: Array<{ value: string; label: string }>; initial?: string }
  tone?: InvestigationTone
  // data-action on the row, `${dataAction}-confirm` on its confirm button
  // (what the e2e harnesses click).
  dataAction?: string
  // Shown but not runnable right now, with this as the tooltip.
  disabledReason?: string
  // Consecutive actions with the same group share a small section header.
  group?: string
}

const TONE: Record<InvestigationTone, { box: string; text: string; confirm: string }> = {
  emerald: { box: 'border-success/40 bg-success-surface', text: 'text-success', confirm: 'border-success bg-success-surface text-success' },
  red: { box: 'border-destructive/40 bg-danger-surface', text: 'text-destructive', confirm: 'border-destructive bg-danger-surface text-destructive' },
  amber: { box: 'border-warning-border bg-warning-surface', text: 'text-warning', confirm: 'border-warning-border bg-warning-surface text-warning' },
  sky: { box: 'border-signal/30 bg-signal-surface', text: 'text-signal', confirm: 'border-signal bg-signal-surface text-signal' },
}

type RunRecord = {
  key: string
  actionId: string
  label: string
  target: string
  status: 'running' | 'success' | 'error'
  result?: InvestigationActionResult
}

// A per-target OSINT-style action panel (WHOIS, DNS, browser load, …),
// built generic enough to drop into any dashboard: the host page owns the
// target string and supplies its own action list (each with its own run()
// against whatever backend actually backs it) — this component only owns
// the run history / in-progress state and the empty state before anything's
// been run. See feedback_backoffice_reusable_widgets: one shared shape,
// reused, rather than a bespoke sidebar per dashboard.
export function InvestigationSidebar({
  theme,
  title = 'Investigation',
  target,
  onTargetChange,
  actions,
  showTarget = true,
  className = '',
  onClose,
}: {
  theme: ProcessedTheme
  title?: string
  target: string
  onTargetChange: (value: string) => void
  actions: InvestigationAction[]
  /** False on a page whose actions all act on the record it is open on (a
   * domain candidate's Verify passes): the Target field is not drawn, and
   * `target` is only the label the run history shows. */
  showTarget?: boolean
  className?: string
  /** Set by EntryDetailShell's ShellPage when this sidebar is rendered as a
   * mobile off-canvas drawer, so it can offer its own close affordance
   * instead of relying solely on the backdrop tap. Omitted (desktop, static
   * column) means no button. */
  onClose?: () => void
}) {
  const c = watchClasses(theme)
  const [runs, setRuns] = useState<RunRecord[]>([])
  const [expandedKey, setExpandedKey] = useState<string | null>(null)
  // The row waiting for its confirm click or its text (confirm / input).
  const [pending, setPending] = useState<{ id: string; value: string; choice?: string } | null>(null)

  const runAction = async (action: InvestigationAction, input?: string, choice?: string) => {
    const trimmedTarget = target.trim()
    if (!action.run) return
    if (action.requiresTarget !== false && !trimmedTarget) return
    setPending(null)
    const key = `${action.id}-${Date.now()}`
    setRuns((prev) => [{ key, actionId: action.id, label: action.label, target: trimmedTarget, status: 'running' }, ...prev])
    setExpandedKey(key)
    try {
      let result = await action.run(trimmedTarget, input, choice)
      if (result.success && action.awaitOutcome) {
        const accepted = result
        setRuns((prev) => prev.map((r) => (r.key === key ? { ...r, result: accepted } : r)))
        result = await action.awaitOutcome(trimmedTarget, accepted)
      }
      setRuns((prev) => prev.map((r) => (r.key === key ? { ...r, status: result.success ? 'success' : 'error', result } : r)))
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      setRuns((prev) => prev.map((r) => (r.key === key ? { ...r, status: 'error', result: { success: false, error: message } } : r)))
    }
  }

  return (
    <div className={`flex h-full flex-col border-r ${c.panel} ${className}`}>
      <div className={`flex items-center justify-between border-b px-4 py-3 ${c.line}`}>
        <span className={`font-mono text-[11px] uppercase tracking-[0.28em] ${c.label}`}>{title}</span>
        <div className="flex items-center gap-2">
          <span className={`font-mono text-[10px] uppercase tracking-widest ${c.faint}`}>
            {runs.length} run{runs.length === 1 ? '' : 's'}
          </span>
          {onClose && (
            <button
              onClick={onClose}
              aria-label="Close investigation sidebar"
              className={`rounded p-1 transition-colors md:hidden ${c.faint} hover:bg-secondary hover:text-muted-foreground`}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {showTarget && (
        <div className={`border-b px-4 py-3 ${c.line}`}>
          <div className={`mb-1.5 font-mono text-[9px] uppercase tracking-widest ${c.faint}`}>Target</div>
          <input
            value={target}
            onChange={(e) => onTargetChange(e.target.value)}
            placeholder="Domain, sender, phone…"
            className={`w-full rounded border px-2.5 py-1.5 font-mono text-[12px] ${c.input}`}
          />
        </div>
      )}

      <div className="overflow-y-auto">
        <div className={`px-4 py-2 font-mono text-[9px] uppercase tracking-widest ${c.faint}`}>Actions</div>
        <div className="space-y-1 px-2 pb-2">
          {actions.map((action, i) => {
            const Icon = action.icon
            const running = runs.some((r) => r.actionId === action.id && r.status === 'running')
            const disabled = !action.run || !!action.disabledReason || running || (action.requiresTarget !== false && !target.trim())
            const asking = pending?.id === action.id
            const tone = action.tone ? TONE[action.tone] : null
            const header = action.group && action.group !== actions[i - 1]?.group ? action.group : null
            const start = () => {
              if (action.confirm || action.input || action.choice) setPending({ id: action.id, value: '', choice: action.choice?.initial ?? action.choice?.options[0]?.value })
              else runAction(action)
            }
            const inputOk = (!action.input || (pending?.value.trim().length ?? 0) >= (action.input.minLength ?? 1))
              && (!action.choice || !!pending?.choice)
            return (
              <Fragment key={action.id}>
                {header && <div className={`px-2.5 pb-1 pt-2 font-mono text-[9px] uppercase tracking-widest ${c.faint}`}>{header}</div>}
                <button
                  type="button"
                  data-action={action.dataAction}
                  onClick={() => (asking ? setPending(null) : start())}
                  disabled={disabled}
                  title={action.disabledReason ?? (action.run ? action.description : 'Not wired up yet')}
                  className={`flex w-full items-center gap-3 rounded-md px-2.5 py-2 text-left transition-colors hover:bg-secondary disabled:cursor-not-allowed disabled:hover:bg-transparent ${!action.run || action.disabledReason ? 'opacity-40' : ''} ${asking ? c.soft : ''}`}
                >
                  <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded border ${tone ? tone.box : `${c.line} ${c.soft}`}`}>
                    {running ? <Loader2 className={`h-4 w-4 animate-spin ${tone ? tone.text : c.faint}`} /> : <Icon className={`h-4 w-4 ${tone ? tone.text : c.faint}`} />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <div className={`font-mono text-[11px] uppercase tracking-widest ${tone ? tone.text : c.text}`}>{action.label}</div>
                    <div className={`truncate text-[10px] ${c.faint}`}>{action.description}</div>
                  </span>
                  {action.run ? (
                    <ChevronRight className={`h-3.5 w-3.5 shrink-0 transition-transform ${asking ? 'rotate-90' : ''} ${c.faint}`} />
                  ) : (
                    <span className={`shrink-0 font-mono text-[8px] uppercase tracking-widest ${c.faint}`}>Soon</span>
                  )}
                </button>
                {asking && (
                  <div className="space-y-2 px-2.5 pb-2 pt-1">
                    {action.choice && (
                      <label className={`flex items-center gap-2 font-mono text-[10px] ${c.faint}`}>
                        <span className="shrink-0">{action.choice.label}</span>
                        <select
                          data-input={action.dataAction ? `${action.dataAction}-choice` : undefined}
                          value={pending.choice ?? ''}
                          onChange={(e) => setPending({ id: action.id, value: pending.value, choice: e.target.value })}
                          className={`min-w-0 flex-1 rounded border px-2 py-1.5 font-mono text-[11px] ${c.input}`}
                        >
                          {action.choice.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </label>
                    )}
                    {action.input && (
                      <input
                        autoFocus
                        data-input={action.dataAction ? `${action.dataAction}-input` : undefined}
                        value={pending.value}
                        onChange={(e) => setPending({ id: action.id, value: e.target.value, choice: pending.choice })}
                        onKeyDown={(e) => { if (e.key === 'Enter' && inputOk) runAction(action, pending.value.trim(), pending.choice) }}
                        placeholder={action.input.placeholder}
                        className={`w-full rounded border px-2.5 py-1.5 font-mono text-[11px] ${c.input}`}
                      />
                    )}
                    <div className="flex gap-2">
                      <button
                        type="button"
                        data-action={action.dataAction ? `${action.dataAction}-confirm` : undefined}
                        disabled={!inputOk}
                        onClick={() => runAction(action, action.input ? pending.value.trim() : undefined, pending.choice)}
                        className={`flex-1 rounded border px-2 py-1 font-mono text-[10px] uppercase tracking-widest transition-colors disabled:opacity-50 ${tone ? tone.confirm : `${c.line} ${c.text}`}`}
                      >
                        {action.confirm ?? 'Confirm'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setPending(null)}
                        className={`rounded border px-2 py-1 font-mono text-[10px] uppercase tracking-widest ${c.line} ${c.faint}`}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </Fragment>
            )
          })}
        </div>
      </div>

      <div className={`mt-auto border-t ${c.line}`}>
        {runs.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-4 py-8 text-center">
            <TargetIcon className={`h-6 w-6 ${c.faint}`} />
            <div className={`font-mono text-[10px] uppercase tracking-widest ${c.faint}`}>No actions run</div>
            <div className={`text-[11px] ${c.faint}`}>{showTarget ? 'Select a target and run an action' : 'Run an action'}</div>
          </div>
        ) : (
          <div className="max-h-64 space-y-1 overflow-y-auto p-2">
            {runs.map((run) => {
              const open = expandedKey === run.key
              return (
                <div key={run.key} className={`rounded border ${c.line}`}>
                  <button
                    type="button"
                    onClick={() => setExpandedKey(open ? null : run.key)}
                    className="flex w-full items-center gap-2 px-2.5 py-2 text-left"
                  >
                    {run.status === 'running' ? (
                      <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-signal" />
                    ) : run.status === 'success' ? (
                      <Check className="h-3.5 w-3.5 shrink-0 text-success" />
                    ) : (
                      <AlertCircle className="h-3.5 w-3.5 shrink-0 text-destructive" />
                    )}
                    <span className="min-w-0 flex-1">
                      <div className={`truncate font-mono text-[10px] uppercase tracking-widest ${c.text}`}>{run.label}</div>
                      <div className={`truncate text-[9px] ${c.faint}`}>{run.target}</div>
                    </span>
                  </button>
                  {open && run.result && (run.result.message && !run.result.error ? (
                    <div data-action-note className={`border-t px-2.5 py-2 text-[11px] leading-relaxed text-success ${c.line}`}>{run.result.message}</div>
                  ) : run.status === 'error' ? (
                    <div data-action-error className={`border-t px-2.5 py-2 text-[11px] leading-relaxed text-destructive ${c.line}`}>{run.result.error ?? 'failed'}</div>
                  ) : (
                    <pre className={`max-h-48 overflow-auto whitespace-pre-wrap break-words border-t px-2.5 py-2 text-[10px] leading-relaxed ${c.line} ${c.faint}`}>
                      {run.result.error ?? JSON.stringify(run.result.data, null, 2)}
                    </pre>
                  ))}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
